import SEO from "@/components/SEO";
import CtaBanner from "@/components/CtaBanner";
import { Users, Briefcase, MapPin, ShieldCheck, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

const CLIENTS = [
  { name: "Tide", color: "#03B5AA" },
  { name: "HDFC AZ Buy", color: "#004C8F" },
  { name: "Mastercard", color: "#EB001B" },
];

const SERVICES = [
  { icon: MapPin, title: "Field Manpower", desc: "Trained field reps for on-ground execution — store visits, surveys, audits, distribution." },
  { icon: Users, title: "Sales Staffing", desc: "Outsourced sales teams for B2B, retail, and direct-to-consumer channels. Fully managed." },
  { icon: Briefcase, title: "Operations Support", desc: "Customer support, back-office ops, fulfilment teams — scaled up or down on demand." },
  { icon: ShieldCheck, title: "Compliance & Payroll", desc: "PF, ESI, statutory compliance, attendance tracking, and end-to-end payroll." },
];

const STATS = [
  { k: "10,000+", v: "Field hours / month" },
  { k: "100+", v: "Cities served" },
  { k: "98%", v: "Compliance score" },
  { k: "24/7", v: "Operations support" },
];

export default function ManpowerSolutions() {
  return (
    <>
      <SEO
        title="Manpower Solutions — Field Teams, Sales Staffing & Operations"
        description="Trusted manpower solutions for Tide, HDFC, AZ Buy and Mastercard. Field manpower, sales staffing and operations support across 100+ Indian cities."
        path="/manpower-solutions"
      />

      <section className="border-b-2 border-black bg-black py-20 text-white md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape">Manpower Solutions</div>
          <h1 className="display mt-6 max-w-4xl text-5xl font-black leading-[0.95] tracking-tight md:text-6xl lg:text-7xl">
            Field teams. Sales staffing.<br /><span className="text-[#FFD700]">Operations at scale.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-white/75 md:text-lg">
            We power on-ground execution for India's biggest brands — fully managed teams, end-to-end compliance, and reporting you can trust.
          </p>
          <div className="mt-10 grid max-w-2xl grid-cols-2 gap-4 md:grid-cols-4">
            {STATS.map((s) => (
              <div key={s.k} className="border-l-2 border-[#FFD700] pl-3">
                <div className="display text-2xl font-black text-[#FFD700] md:text-3xl">{s.k}</div>
                <div className="text-xs font-medium text-white/70">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT LOGOS */}
      <section className="border-b-2 border-black bg-white py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="text-center text-xs font-bold uppercase tracking-[0.25em] text-black/60">
            Trusted by industry leaders
          </div>
          <div className="mt-8 grid grid-cols-1 gap-0 md:grid-cols-3">
            {CLIENTS.map((c) => (
              <div
                key={c.name}
                data-testid={`client-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="-ml-[2px] -mt-[2px] flex aspect-[5/2] items-center justify-center border-2 border-black bg-white"
              >
                <div className="display text-3xl font-black tracking-tight md:text-4xl" style={{ color: c.color }}>
                  {c.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#FAFAF7] py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="eyebrow">What we deliver</div>
          <h2 className="display mt-3 text-3xl font-black tracking-tight md:text-4xl">Capabilities across India.</h2>
          <div className="mt-10 grid grid-cols-1 gap-0 md:grid-cols-2">
            {SERVICES.map((s, i) => (
              <div
                key={s.title}
                data-testid={`manpower-service-${i}`}
                className="-ml-[2px] -mt-[2px] flex gap-5 border-2 border-black bg-white p-8 hover:bg-[#FFD700]"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center border-2 border-black bg-[#FFD700]">
                  <s.icon size={22} />
                </div>
                <div>
                  <h3 className="display text-xl font-black">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-black/75">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <h2 className="display text-3xl font-black tracking-tight md:text-4xl">Why brands choose Orina.</h2>
          <ul className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
            {[
              "Pan-India coverage across 100+ cities",
              "Vetted, trained and certified workforce",
              "Real-time field reporting via Worknex app",
              "Statutory compliance handled end-to-end",
              "SLA-driven engagement with weekly reviews",
              "Flexible monthly contracts — scale up or down",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3 border-2 border-black bg-white p-4">
                <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
                <span className="text-sm md:text-base">{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-10">
            <Link to="/contact/?type=manpower" className="btn-brutal">
              Talk to Manpower Team <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <CtaBanner
        title="Need a field or sales team this month?"
        subtitle="We can deploy trained teams across 100+ cities in 14 days. Tell us your scope."
        primaryLabel="Request Proposal"
        primaryTo="/contact/?type=manpower"
      />
    </>
  );
}
